import PropTypes from 'prop-types'
import React from 'react'
import { findDOMNode } from 'react-dom'
import Helmet from 'react-helmet'
import { DropTarget } from 'react-dnd'
import { NativeTypes } from 'react-dnd-html5-backend'
import { runSaga } from 'store/sagaMiddleware'
import viewChannelData from '../sagas/viewChannelData'
import getCharCount from 'components/RichText/utils/getCharCount'
import { dragType as documentDragType } from 'components/Document/Document'
import Scrollzone from 'components/Scrollzone'
import Scrollbars from 'components/Scrollbars'
import Sortable from 'components/Sortable'
import ElementContainer from '../ElementContainer'
import Activities from '../Activities'
import Issues from './Issues'
import Completed from './Completed'
import Board from './Board'
import List from './List'
import MyBoard from './MyBoard'
import Status from './Status'
import Users from './Users'
import Labels from './Labels'
import Dates from './Dates'
import AddElement from './AddElement'
import Message from './Message'
import classes from './View.module.scss'
import { HotKeys } from 'react-hotkeys'
import HistoryContext from 'components/App/HistoryContext'
import { withBoardPermission } from 'dataProviders/Board'
import CardRelations from './CardRelations'

const draggableOverStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: '#ccc',
  zIndex: 5,
  opacity: 0.5,
  pointerEvents: 'none',
}

export class View extends React.Component {
  static propTypes = {
    permission: PropTypes.object.isRequired,

    isArchived: PropTypes.bool.isRequired,
    id: PropTypes.number.isRequired,
    cardIdsInList: PropTypes.array.isRequired,
    name: PropTypes.string.isRequired,
    elements: PropTypes.array.isRequired,
    isTemplate: PropTypes.bool.isRequired,

    fetchHeartbeats: PropTypes.func.isRequired,
    requestPositionElement: PropTypes.func.isRequired,
    requestHeartbeat: PropTypes.func.isRequired,
    requestUploadStart: PropTypes.func.isRequired,
    requestMarkTaggedNotificationsAsRead: PropTypes.func.isRequired,
    requestAddDocumentToCard: PropTypes.func.isRequired,

    connectDocumentDropTarget: PropTypes.func.isRequired,
    isDocumentOver: PropTypes.bool.isRequired
  }

  static contextType = HistoryContext

  elementRefs = {}

  state = {
    isEditingMessage: false
  }

  static keyMap = {
    'goPrevious': 'p',
    'goNext': 'n'
  }

  componentWillMount () {
    this.viewChannelDataSaga = runSaga(viewChannelData, { cardId: this.props.id })
  }

  componentDidMount () {
    const {
      id,
      fetchHeartbeats,
      requestMarkTaggedNotificationsAsRead,
      requestHeartbeat
    } = this.props

    requestMarkTaggedNotificationsAsRead({
      tag: 'card-' + id
    })

    fetchHeartbeats({
      location: `card-${id}`
    })

    requestHeartbeat({
      heartbeats: [{ location: `card-${id}`, action: 'Enter' }]
    })
    this.heartbeatInterval = setInterval(this.heartbeat, 20000) // 20 seconds
  }

  componentDidUpdate (prevProps, prevState) {
    const { id, requestHeartbeat } = this.props
    const { isEditingMessage } = this.state

    if (isEditingMessage !== prevState.isEditingMessage) {
      requestHeartbeat({
        heartbeats: [{ location: `card-${id}`, action: isEditingMessage ? 'StartEdit' : 'StopEdit' }]
      })
    }
  }

  componentWillUnmount () {
    const { id, requestHeartbeat } = this.props
    const { isEditingMessage } = this.state

    requestHeartbeat({
      heartbeats: [
        { location: `card-${id}`, action: 'Leave' },
        ...(
          isEditingMessage
            ? [
              { location: `card-${id}`, action: 'StopEdit' }
            ]
            : []
        )
      ]
    })

    this.viewChannelDataSaga.cancel()

    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      delete this.heartbeatInterval
    }

    if (this.fetchSubCardGroupsProgressInterval) {
      clearInterval(this.fetchSubCardGroupsProgressInterval)
      delete this.fetchSubCardGroupsProgressInterval
    }
  }

  heartbeat = () => {
    const { id, requestHeartbeat } = this.props
    const { isEditingMessage } = this.state

    requestHeartbeat({
      heartbeats: [
        { location: `card-${id}`, action: 'Being' },
        ...(
          isEditingMessage
            ? [
              { location: `card-${id}`, action: 'Editing' }
            ]
            : []
        )
      ]
    })
  }

  handleMessageChange = ({ message }) => {
    if (
      !this.state.isEditingMessage &&
      getCharCount(message) > 0
    ) {
      this.setState({
        isEditingMessage: true
      })
    }
  }

  handleMessageBlur = () => {
    if (this.state.isEditingMessage) {
      this.setState({
        isEditingMessage: false
      })
    }
  }

  handleElementPositionChange = ({ itemId, position }) => {
    this.props.requestPositionElement({
      cardId: this.props.id,
      elementId: itemId,
      position
    })
  }

  goToNextCard = () => {
    const {
      id,
      cardIdsInList,
    } = this.props

    if (!cardIdsInList) return

    const index = cardIdsInList.indexOf(id)

    if (index + 1 < cardIdsInList.length) {
      this.context.replace('/card/' + cardIdsInList[index + 1])
    }
  }

  goToPreviousCard = () => {
    const {
      id,
      cardIdsInList,
    } = this.props

    if (!cardIdsInList) return

    const index = cardIdsInList.indexOf(id)

    if (index - 1 >= 0) {
      this.context.replace('/card/' + cardIdsInList[index - 1])
    }
  }

  keyHandlers = {
    'goPrevious': this.goToPreviousCard,
    'goNext': this.goToNextCard
  }

  renderElement = React.forwardRef((props, ref) => {
    return (
      <ElementContainer
        ref={ref}
        elementRef={(elementRef) => {
          if (elementRef) {
            this.elementRefs[props.id] = elementRef
          } else if (this.elementRefs[props.id]) {
            delete this.elementRefs[props.id]
          }
        }}
        {...props}
      />
    )
  })

  render () {
    const {
      permission,
      isArchived,
      name,
      elements,
      isTemplate,
      isDocumentOver,
      connectDocumentDropTarget,
    } = this.props

    let className = classes.view
    if (isArchived) className += ' ' + classes.archived

    return (
      <Scrollbars
        ref={ref => connectDocumentDropTarget(findDOMNode(ref))}
        id='viewContent'
        className={className}
      >
        {!isArchived &&
          <HotKeys
            focused
            keyMap={View.keyMap}
            handlers={this.keyHandlers}
            attach={window}
          />
        }
        <Helmet
          defer={false}
        >
          <title>{name}</title>
        </Helmet>
        {isDocumentOver && <span style={draggableOverStyle} />}
        <div className={classes.column} style={{ maxWidth: '300px' }}>
          <Scrollbars
            className={`${classes.columnContent} ${classes.leftPanel}`}
          >
            <Issues />
            <Board />
            <Status />
            <Completed />
            <List />
            {!isTemplate &&
              <MyBoard />
            }
            <Users />
            <Labels />
            <Dates />
            {!isTemplate &&
              <CardRelations />
            }
          </Scrollbars>
        </div>
        <div className={classes.column}>
          <div className={classes.columnHeader}>
            {(!isArchived && permission.visitor) &&
              <Message
                onChange={this.handleMessageChange}
                onBlur={this.handleMessageBlur}
              />
            }
          </div>
          <div className={`${classes.columnContent} ${classes.activities}`}>
            <Activities />
          </div>
        </div>
        <div className={classes.column} style={{ minWidth: '330px' }}>
          {(!isArchived && permission.member) &&
            <div className={classes.columnHeader + ' m-b-15'}>
              <AddElement />
            </div>
          }
          <Scrollzone
            vertical
            component={Scrollbars}
            className={classes.columnContent}
          >
            <Sortable
              vertical
              className={classes.elementsContainer}
              name={'cardElements'}
              component={this.renderElement}
              items={elements}
              disabled={isArchived || !permission.member}
              onPositionChange={this.handleElementPositionChange}
            />
          </Scrollzone>
        </div>
      </Scrollbars>
    )
  }
}

const documentDropTarget = {
  canDrop (props) {
    return props.permission.member
  },

  drop (props, monitor, component) {
    const { id, requestAddDocumentToCard, requestUploadStart } = props
    const { files, url, documentSourceId } = monitor.getItem()

    if (monitor.didDrop()) return

    if (files) {
      var count = files.length

      if (count > 0) {
        for (var i = 0; i < count; i++) {
          requestUploadStart({
            file: files[i],
            cardId: id,
            onUpload: ({ elementId }) => {
              if (
                component &&
                component.elementRefs &&
                component.elementRefs[elementId] &&
                component.elementRefs[elementId].refresh
              ) {
                component.elementRefs[elementId].refresh()
              }
            }
          })
        }
      }
    } else {
      requestAddDocumentToCard({
        cardId: id,
        url,
        documentSourceId
      })
    }
  }
}

export default
withBoardPermission(DropTarget(
  [NativeTypes.FILE, documentDragType],
  documentDropTarget,
  (connect, monitor) => ({
    connectDocumentDropTarget: connect.dropTarget(),
    isDocumentOver: monitor.isOver({ shallow: true })
  })
)(View))
